import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Paper,
} from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import BorderColorOutlined from '@mui/icons-material/BorderColorOutlined';
import CpaxLogo from '../../components/CpaxLogo';

// Clickable option row matching the Figma bordered alert style
interface OptionRowProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

function OptionRow({ icon, label, onClick }: OptionRowProps) {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: '6px',
        borderRadius: 1,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          bgcolor: 'action.hover',
        },
        width: '100%',
      }}
    >
      {/* Icon container */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          pr: '12px',
          py: '7px',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      {/* Label */}
      <Box sx={{ flex: '1 0 0', py: 1 }}>
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: '1rem',
            lineHeight: 1.5,
            letterSpacing: '0.15px',
            color: 'text.secondary',
          }}
        >
          {label}
        </Typography>
      </Box>

      {/* Chevron */}
      <ChevronRight sx={{ color: 'action.active', flexShrink: 0 }} />
    </Paper>
  );
}

const steps = ['Background', 'Confirmation'];

export default function IndividualBackgroundPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const planData = location.state?.planData;
  const profileData = location.state?.profileData;

  const handleManual = () => {
    navigate('/auth/individual-background-manual', { state: { planData, profileData } });
  };

  const handleBack = () => {
    navigate('/auth/individual-plans', { state: { planData } });
  };

  const handleSkip = () => {
    navigate('/app/assistant');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'surface.subtle',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top AppBar — logo only */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: 1,
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CpaxLogo height={28} showMark={false} />
          </Box>
        </Toolbar>
      </AppBar>

      {/* Stepper AppBar — Background (active) → Confirmation */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3 }, justifyContent: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: 852 }}>
            <Stepper activeStep={0}>
              {steps.map((label, index) => (
                <Step key={label} completed={index === 0 && false}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main content — centered */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 4, sm: 8 },
          px: { xs: 2, sm: 0 },
          pb: { xs: '80px', sm: '128px' },
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 608,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Page title */}
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: 24, sm: 32 },
              fontWeight: 400,
              lineHeight: 1.235,
              letterSpacing: '0.25px',
              color: 'primary.main',
              textAlign: 'center',
              mb: 1,
            }}
          >
            Add your work history and certifications
          </Typography>

          {/* Subtitle */}
          <Typography
            variant="body2"
            sx={{
              fontSize: 14,
              lineHeight: 1.43,
              letterSpacing: '0.17px',
              color: 'text.primary',
              textAlign: 'center',
              mb: 4,
            }}
          >
            This helps us determine what courses and credits to recommend. It's quickest to import
            your information. You can also add this in later on your profile settings page.
          </Typography>

          {/* Option rows */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: '100%',
            }}
          >
            <OptionRow
              icon={
                <BorderColorOutlined sx={{ color: 'action.active', width: 24, height: 24 }} />
              }
              label="Fill out manually (15 min)"
              onClick={handleManual}
            />
          </Box>
        </Box>
      </Box>

      {/* Bottom footer bar — fixed at bottom */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          top: 'auto',
          bottom: 0,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          height: { xs: 'auto', sm: 128 },
        }}
      >
        <Toolbar
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 0 },
            justifyContent: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Button
            variant="outlined"
            size="large"
            onClick={handleBack}
            sx={{
              px: '22px',
              py: 1,
              fontSize: '0.9375rem',
              fontWeight: 500,
              letterSpacing: '0.46px',
              borderColor: 'rgba(86, 151, 55, 0.5)',
            }}
          >
            Back
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={handleSkip}
            sx={{
              px: '22px',
              py: 1,
              fontSize: '0.9375rem',
              fontWeight: 500,
              letterSpacing: '0.46px',
              borderColor: 'rgba(86, 151, 55, 0.5)',
            }}
          >
            Skip for now
          </Button>
        </Toolbar>
      </AppBar>
    </Box>
  );
}
